import api, { resetLogoutFlag } from '../utils/api';
import auth from '../utils/auth';

class AuthService {
  // Login user
  async login(usernameOrEmail, password) {
    try {
      const response = await api.post('/auth/login', { usernameOrEmail, password });
      
      if (!response.data.user) {
        throw new Error('User data not found in login response');
      }

      const { user, jwt } = response.data;
      auth.setUser(user);
      
      return { user, jwt };
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Register user
  async register(userData) {
    try {
      const response = await api.post('/users/register', userData);
      
      if (!response.data.data) {
        throw new Error('User data not found in registration response');
      }

      const { data: user } = response.data;
      auth.setUser(user);
      
      return { user };
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Check current authentication status
  async checkAuth() {
    try {
      const localUser = auth.getUser();
      
      if (!localUser) {
        throw new Error('No local user found');
      }

      // Check if session is expired
      if (auth.isSessionExpired()) {
        auth.clearAuth();
        throw new Error('Session expired');
      }

      const response = await api.get('/users/me');
      const user = response.data.data; // ApiEnvelope wrapper
      
      if (!user) {
        throw new Error('Invalid user data from server');
      }

      // Update user data and extend session
      auth.setUser(user);
      return { user };
    } catch (error) {
      // Only clear auth if it's a session expiry or no local user
      // Don't clear on 401 errors as they might be temporary
      if (error.message === 'No local user found' || error.message === 'Session expired') {
        auth.clearAuth();
      }
      throw new Error('Authentication check failed');
    }
  }

  // Logout user
  async logout() {
    try {
      // No server-side logout endpoint exists - just clear local auth data
      // The backend uses cookie-based authentication, so clearing the cookie
      // is handled by the browser when the session expires
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      // Always clear local auth data
      auth.clearAuth();
      // Reset the logout flag after a short delay to allow pending requests to complete
      setTimeout(() => {
        resetLogoutFlag();
      }, 1000);
    }
  }

  // Get current user from local storage
  getCurrentUser() {
    const user = auth.getUser();
    return user;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getCurrentUser();
  }

  // Check if session is expired
  isSessionExpired() {
    return auth.isSessionExpired();
  }

  // Get session expiry date
  getSessionExpiry() {
    return auth.getSessionExpiry();
  }

  // Get remaining session time in milliseconds
  getRemainingSessionTime() {
    return auth.getRemainingSessionTime();
  }

  // Extend session
  extendSession() {
    return auth.extendSession();
  }

  // Check if user has specific role
  hasRole(role) {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;
    return user.roles.includes(role.toUpperCase());
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles) {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;
    return roles.some(role => user.roles.includes(role.toUpperCase()));
  }

  // Check if user has all of the specified roles
  hasAllRoles(roles) {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;
    return roles.every(role => user.roles.includes(role.toUpperCase()));
  }

  // Check if user is admin
  isAdmin() {
    return this.hasRole('ADMIN');
  }

  // Check if user is site owner
  isSiteOwner() {
    return this.hasRole('SITE_OWNER');
  }

  // Check if user is court owner
  isCourtOwner(courtOwnerId) {
    const user = this.getCurrentUser();
    return user?.id === courtOwnerId;
  }

  // Get user permissions for a specific resource
  getUserPermissions(resourceOwnerId) {
    const user = this.getCurrentUser();
    
    if (!user) {
      return {

        canManage: false
      };
    }

    const isOwner = user.id === resourceOwnerId;
    const isAdminUser = this.isAdmin();
    const isSiteOwnerUser = this.isSiteOwner();

    return {
      canView: true, // Authenticated users can view
      canEdit: isOwner || isAdminUser || isSiteOwnerUser,

      canManage: isOwner || isAdminUser || isSiteOwnerUser
    };
  }

  // Refresh user data from server
  async refreshUser() {
    try {
      const response = await api.get('/users/me');
      const user = response.data.data; // ApiEnvelope wrapper
      
      if (user) {
        auth.setUser(user);
        return user;
      }
      
      throw new Error('Failed to refresh user data');
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }

  // Update user profile using /me endpoint
  async updateProfile(userData) {
    try {
      // Convert dob to yyyy-MM-dd format as expected by backend
      const processedData = { ...userData };
      if (processedData.dob && typeof processedData.dob === 'string') {
        // Convert from ISO string or date to yyyy-MM-dd format
        const date = new Date(processedData.dob);
        processedData.dob = date.toISOString().split('T')[0]; // yyyy-MM-dd
      }
      
      const response = await api.patch('/users/me', processedData);
      const updatedUser = response.data.data; // ApiEnvelope wrapper
      
      if (updatedUser) {
        auth.setUser(updatedUser);
        return updatedUser;
      }
      
      throw new Error('Failed to update profile');
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Change password using /me/password endpoint
  async changePassword(newPassword) {
    try {
      await api.post('/users/me/password', {
        newPassword
      });
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Request password reset
  async requestPasswordReset(email) {
    try {
      await api.post('/auth/forgot', { email });
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Reset password with token
  async resetPassword(token, newPassword) {
    try {
      await api.post('/auth/reset', {
        token,
        newPassword
      });
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Verify email with token using new endpoint
  async verifyEmail(token) {
    try {
      const response = await api.post(`/users/verify-email?token=${token}`);
      const user = response.data.data; // ApiEnvelope wrapper
      
      if (user) {
        auth.setUser(user);
        return user;
      }
      
      throw new Error('Failed to verify email');
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Resend verification email - not implemented in backend yet
  async resendVerificationEmail(email) {
    throw new Error('Resend verification email is not implemented yet. Please contact support.');
  }

  // Get current user profile using /me endpoint
  async getMyProfile() {
    try {
      const response = await api.get('/users/me');
      return response.data.data;
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Search user by username or email
  async searchUser(identifier) {
    try {
      console.log('Searching for user:', identifier);
      const response = await api.get(`/users/search?identifier=${encodeURIComponent(identifier)}`);
      console.log('Search response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('Search user error:', error);
      
      // If the endpoint doesn't exist yet, return a helpful message
      if (error.response?.status === 404) {
        throw new Error('User search endpoint not available yet. Please contact support.');
      }
      
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Delete my account using /me endpoint
  async deleteMyAccount() {
    try {
      await api.delete('/users/me');
      auth.clearAuth();
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Get purchased videos for current user
  async getMyPurchasedVideos() {
    try {
      const user = this.getCurrentUser();
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }
      
      const response = await api.get(`/users/${user.id}/purchased-videos`);
      
      // Handle backend error responses that return null
      if (response.data === null) {
        return []; // Return empty array if no videos found
      }
      
      return response.data.data || []; // ApiEnvelope wrapper or empty array
    } catch (error) {
      const errorMessage = this._extractErrorMessage(error);
      throw new Error(errorMessage);
    }
  }

  // Extract error message from API response
  _extractErrorMessage(error) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred';
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService; 